import { Conversation } from '@elevenlabs/client';
import { OpenAIRealtimeWebRTC, RealtimeAgent, RealtimeSession } from '@openai/agents/realtime';
import { toast } from 'sonner';
import {
	AgentConfig,
	OpenAIVoice,
	PitchType,
	Provider,
	TranscriptEntry,
	VoiceAgentInterface,
	VoiceGender,
} from '../types';

export class VoiceAgent implements VoiceAgentInterface {
	private agent: RealtimeAgent;
	private session: RealtimeSession<any> | null = null;
	private apiKey: string;
	private provider: Provider;
	private transcript: TranscriptEntry[] = [];
	private onTranscriptUpdate?: (transcript: TranscriptEntry[]) => void;
	private onNewTranscriptEntry?: (entry: TranscriptEntry) => void;
	private elevenLabsConversation: Conversation | null = null;
	private conversationId: string | null = null;

	constructor(config: AgentConfig, _useRandomization = true) {
		this.apiKey = config.apiKey;
		this.provider = config.provider || 'openai';
		this.onTranscriptUpdate = config.onTranscriptUpdate;
		this.onNewTranscriptEntry = config.onNewTranscriptEntry;
		// Agent will be created in connect() method with proper voice
		this.agent = new RealtimeAgent({
			name: 'SalesProspect',
			instructions: '',
			voice: 'alloy', // Default, will be overridden in connect
		});
	}

	private getVoiceForGender(gender: VoiceGender): OpenAIVoice {
		// Map gender to appropriate OpenAI voices
		return gender === 'male' ? 'ash' : 'alloy';
	}

	async connect(pitchType: PitchType, customPrompt?: string, voiceGender?: VoiceGender): Promise<void> {
		if (this.provider === 'openai') {
			await this.connectOpenAI(pitchType, customPrompt, voiceGender);
		} else if (this.provider === 'elevenlabs') {
			await this.connectElevenLabs(pitchType, customPrompt, voiceGender);
		} else {
			throw new Error(`Unsupported provider: ${this.provider}`);
		}
	}

	private async connectOpenAI(pitchType: PitchType, customPrompt?: string, voiceGender?: VoiceGender): Promise<void> {
		// Determine voice based on gender (default to Sarah/alloy if not specified)
		const selectedVoice = voiceGender ? this.getVoiceForGender(voiceGender) : 'alloy';

		// Always create agent with correct voice
		this.agent = new RealtimeAgent({
			name: 'SalesProspect',
			instructions: customPrompt,
			voice: selectedVoice,
		});

		const webrtcTransport = new OpenAIRealtimeWebRTC({
			useInsecureApiKey: true,
		});

		this.session = new RealtimeSession(this.agent, {
			transport: webrtcTransport,
		});

		// Add timeout to prevent hanging
		const connectPromise = this.session.connect({ apiKey: this.apiKey });
		const timeoutPromise = new Promise((_, reject) => {
			setTimeout(() => reject(new Error('Connection timeout after 30 seconds')), 30000);
		});

		await Promise.race([connectPromise, timeoutPromise]);
		this.setupTranscriptCapture();
	}

	private async connectElevenLabs(
		pitchType: PitchType,
		customPrompt?: string,
		_voiceGender?: VoiceGender
	): Promise<void> {
		// Detect connection type based on apiKey format:
		// - Signed URLs start with "wss://" -> use WebSocket
		// - Conversation tokens don't -> use WebRTC
		const isSignedUrl = this.apiKey.startsWith('wss://');

		const options = {
			// Dynamic auth: signedUrl for WebSocket, conversationToken for WebRTC
			...(isSignedUrl ? { signedUrl: this.apiKey } : { conversationToken: this.apiKey }),
			overrides: {
				agent: {
					prompt: {
						prompt: customPrompt || `You are a sales prospect. Act as a potential customer for a ${pitchType} call.`,
					},
					// Blank out the first message to let the sales rep initiate the conversation
					firstMessage: '',
				},
			},
			onConnect: ({ conversationId }: { conversationId: string }) => {
				this.conversationId = conversationId;
				console.log(`Connected to ElevenLabs via ${isSignedUrl ? 'WebSocket' : 'WebRTC'}`);
				toast.success('Connected to ElevenLabs');
			},
			onMessage: ({ message, source }: { message: string; source: string }) => {
				const transcriptEntry: TranscriptEntry = {
					speaker: source === 'user' ? 'Sales Rep' : 'Prospect',
					message: message || '',
					timestamp: new Date(),
				};

				this.transcript.push(transcriptEntry);

				if (this.onTranscriptUpdate) {
					this.onTranscriptUpdate([...this.transcript]);
				}

				if (this.onNewTranscriptEntry) {
					this.onNewTranscriptEntry(transcriptEntry);
				}
			},
		};

		// Type assertion needed due to discriminated union in SDK types
		this.elevenLabsConversation = await Conversation.startSession(
			options as Parameters<typeof Conversation.startSession>[0]
		);
	}
	private setupTranscriptCapture(): void {
		if (!this.session) {
			return;
		}

		// Set up event listeners for transcript capture
		const eventNames = [
			'history_updated',
			'history_added',
			'conversation_updated',
			'message_added',
			'transcript_updated',
			'conversation.item.created',
			'response.done',
			'conversation.item.input_audio_transcription.completed',
		];

		eventNames.forEach(eventName => {
			(this.session as any).on(eventName, (...args: any[]) => {
				// Try to extract transcript from any event structure
				if (eventName === 'history_updated' && Array.isArray(args[0])) {
					this.processHistoryArray(args[0]);
				}
			});
		});
	}

	private processHistoryArray(history: any[]): void {
		const processedItemIds = new Set(this.transcript.map(entry => (entry as any).itemId));

		history.forEach((item: any) => {
			if (item.type === 'message' && !processedItemIds.has(item.itemId)) {
				// For user messages, wait for completed status
				// For assistant messages, process if they have transcript content (even if in_progress)
				const shouldProcess =
					item.status === 'completed' || (item.role === 'assistant' && this.hasTranscriptContent(item));

				if (shouldProcess) {
					this.processHistoryItemForTranscript(item);
				}
			}
		});
	}

	private hasTranscriptContent(item: any): boolean {
		if (!item.content || !Array.isArray(item.content)) return false;

		return item.content.some((content: any) => {
			return (
				(content.type === 'audio' && content.transcript) ||
				(content.type === 'input_audio' && content.transcript) ||
				(content.type === 'text' && content.text)
			);
		});
	}

	private processHistoryItemForTranscript(item: any): void {
		if (!item || item.type !== 'message') return;

		const speaker = item.role === 'user' ? 'Sales Rep' : 'Prospect';
		let message = '';

		// Extract text from content array based on the structure we saw in debug
		if (item.content && Array.isArray(item.content)) {
			for (const content of item.content) {
				if (content.type === 'input_audio' && content.transcript) {
					message += content.transcript;
				} else if (content.type === 'audio' && content.transcript) {
					message += content.transcript;
				} else if (content.type === 'text' && content.text) {
					message += content.text;
				}
			}
		}

		if (message.trim()) {
			const transcriptEntry: any = {
				speaker,
				message: message.trim(),
				timestamp: new Date(),
				itemId: item.itemId, // Store itemId to prevent duplicates
			};

			this.transcript.push(transcriptEntry);

			// Notify UI of transcript update
			if (this.onTranscriptUpdate) {
				this.onTranscriptUpdate([...this.transcript]);
			}

			// Notify of single entry
			if (this.onNewTranscriptEntry) {
				this.onNewTranscriptEntry(transcriptEntry);
			}
		}
	}

	getTranscript(): TranscriptEntry[] {
		return [...this.transcript];
	}

	getFormattedTranscript(): string {
		return this.transcript
			.map(entry => {
				const time = entry.timestamp.toLocaleTimeString();
				return `[${time}] ${entry.speaker}: ${entry.message}`;
			})
			.join('\n');
	}

	clearTranscript(): void {
		this.transcript = [];
		if (this.onTranscriptUpdate) {
			this.onTranscriptUpdate([]);
		}
	}

	getConversationId(): string | null {
		return this.conversationId;
	}

	disconnect(): void {
		if (this.session) {
			this.session.close();
			this.session = null;
		}

		if (this.elevenLabsConversation) {
			// Clean up ElevenLabs conversation
			this.elevenLabsConversation.endSession();
			this.elevenLabsConversation = null;
		}

		// Clear conversationId on disconnect
		this.conversationId = null;
	}

	private setupElevenLabsTranscriptCapture(): void {
		// ElevenLabs transcript capture is already set up in the connectElevenLabs method
		// via the callback functions passed to Conversation.startSession()
	}
}
